import * as React from 'react';

import {
    ApplicationHelpType,
    AssertionDescriptorCategories,
    AssertionDescriptorUsageTypesEnum,
    DataScopesEnum,
    DocumentExportFormat,
    EntityTypes,
    LookupDataEnums,
    NoteCategories,
    NotePurposeTypesEnum,
    NoteStatusTypes,
    NoteTypes,
    StrategyTypes,
    SystemGeneratedDocumentsTypesEnum,
    UserRightsEnum,
    ZoneContextType,
} from '../../utilities/Constants';
import { DocumentSubTypeEnum, DocumentTypeEnum } from '../../utilities/DbConstants';
import {
    IAssertionDescriptorModel,
    IEntityAssertionModel,
} from '../../interfaces/IAssertionDescriptor';
import { INoteCategoryTypeModel, INoteModel, INotesUICustomization } from '../../interfaces/INote';
import { Modal, Stack } from 'react-bootstrap';

import { ApiRoutes } from '../../utilities/ApiRoutes';
import Authorization from '../../stores/Authorization';
import { CheckmateDialog } from '../../components/shared/dialog';
import Common from '../../stores/Common';
import { DisplayMessages } from '../../utilities/DisplayMessages';
import { EditNarrativeModal } from './EditNarrativeModal';
import EntityAssertionControl from '../../components/shared/EntityAssertionControl';
import { ExportFileButton } from '../../components/shared/ExportFileButton';
import { Help } from '../../components/shared/Help';
import { IOrganizationModel } from '../../interfaces/IOrganization';
import { IUserModel } from '../../interfaces/IUser';
import { IValidation } from '../../interfaces/IError';
import { Loader } from '../../components/shared/Loader';
import { LocalRoutes } from '../../utilities/LocalRoutes';
import { MainContentOverlayComponent } from '../../components/shared/MainContentOverlay';
import { NoteEditor } from '../../components/notes/note-editor/NoteEditor';
import NoteHelper from '../../utilities/NoteHelper';
import { PageWithAffixedHeader } from '../../shared/PageWithAffixedHeader';
import Sort from '../../stores/Sort';
import { SortableHeader } from '../../components/shared/SortableHeader';
import ValidateUtils from '../../shared/validations';
import classNames from 'classnames';
import { cloneDeep } from 'lodash';

interface IStrategicPlanProps {
    assertionDescriptorsStrategy: IAssertionDescriptorModel[];
    assertionDescriptorsSignificantChanges: IAssertionDescriptorModel[];
    user: IUserModel;
    refreshIdentifier?: string;
    zoneContext: ZoneContextType;
}

interface IStrategicPlanState {
    pendingResponse: boolean;
    readOnly: boolean;
    validation: IValidation;
    assertions: IEntityAssertionModel[];
    originalAssertions: IEntityAssertionModel[];
    currentNarrativeCaseAssertionOnEditMode?: IEntityAssertionModel;
    significantChanges: INoteModel[];
    originalSignificantChanges: INoteModel[];
    showSignificantChanges?: boolean;
    selectedSignificantChange?: INoteModel;
    openNoteEditor?: boolean;
    noteCategoryTypes: INoteCategoryTypeModel[];
    defaultOrganization: IOrganizationModel;
    showConfirmRemoveSignificantChangeDialog?: boolean;
    selectedSignificantChangeToDelete?: INoteModel;
    loggedInUserDataScopeId: number;
}

export class StrategicPlan extends React.Component<IStrategicPlanProps, IStrategicPlanState> {
    constructor(props: any) {
        super(props);

        this.state = {
            pendingResponse: true,
            readOnly: true,
            validation: {},
            assertions: [],
            originalAssertions: [],
            noteCategoryTypes: [],
            defaultOrganization: {},
            significantChanges: [],
            originalSignificantChanges: [],
            loggedInUserDataScopeId: 0,
        };
    }

    componentDidMount() {
        if (
            !Authorization.isAuthorizedToRoute(
                LocalRoutes.StrategicPlan,
                this.props.user,
                this.props.zoneContext.zoneGuid
            )
        )
            window.location.assign(LocalRoutes.AccessDenied);
        this.loadInitialData();
    }

    componentDidUpdate(prevProps: IStrategicPlanProps) {
        if (
            this.props.refreshIdentifier &&
            prevProps.refreshIdentifier != this.props.refreshIdentifier
        )
            this.setState(
                {
                    pendingResponse: true,
                    readOnly: true,
                    validation: {},
                    assertions: [],
                    originalAssertions: [],
                    noteCategoryTypes: [],
                    defaultOrganization: {},
                    significantChanges: [],
                    originalSignificantChanges: [],
                    loggedInUserDataScopeId: 0,
                    openNoteEditor: false,
                    selectedSignificantChange: undefined,
                    showSignificantChanges: false,
                },
                this.loadInitialData
            );
    }

    loadInitialData = async () => {
        const strategicPlanDataResponse = await this.getStrategicPlanAssertions();

        const noteCategoryTypesResponse =
            await this.props.zoneContext.zoneApiClient.getNoteCategoryTypes(
                LookupDataEnums.SignificantChangesCategories
            );
        if (noteCategoryTypesResponse.httpResponse.status == 401) {
            window.location.reload();
            return;
        }
        if (noteCategoryTypesResponse.errorMessage) {
            this.setState({
                validation: ValidateUtils.parseErrors(
                    noteCategoryTypesResponse.errors,
                    noteCategoryTypesResponse.errorMessage
                ),
                pendingResponse: false,
            });
            return;
        }

        const sigChangesResponse = await this.props.zoneContext.zoneApiClient.getNotes(
            NoteTypes.StrategySignificantChange,
            NoteCategories.StrategySignificantChanges.toString()
        );
        if (sigChangesResponse.httpResponse.status == 401) {
            window.location.reload();
            return;
        }
        if (sigChangesResponse.errorMessage) {
            this.setState({
                validation: ValidateUtils.parseErrors(
                    sigChangesResponse.errors,
                    sigChangesResponse.errorMessage
                ),
                pendingResponse: false,
            });
            return;
        }

        const userDefaultOrgResponse =
            await this.props.zoneContext.zoneApiClient.getCurrentUserOrganizationFromServer(
                this.props.zoneContext.zoneGuid
            );
        if (!userDefaultOrgResponse) return;
        if (userDefaultOrgResponse.httpResponse.status == 401) {
            window.location.reload();
            return;
        }
        if (userDefaultOrgResponse.errorMessage) {
            this.setState({
                validation: ValidateUtils.parseErrors(
                    userDefaultOrgResponse.errors,
                    userDefaultOrgResponse.errorMessage
                ),
                pendingResponse: false,
            });
            return;
        }

        const loggedInUserDataScopeId = Authorization.getUserDefaultOrganizationDataScope(
            this.props.user,
            this.props.zoneContext.zoneGuid
        );

        this.setState({
            pendingResponse: false,
            readOnly: true,
            assertions: strategicPlanDataResponse!.payload!,
            originalAssertions: JSON.parse(JSON.stringify(strategicPlanDataResponse!.payload)),
            noteCategoryTypes: noteCategoryTypesResponse.payload!,
            significantChanges: sigChangesResponse.payload!,
            originalSignificantChanges: JSON.parse(JSON.stringify(sigChangesResponse.payload!)),
            defaultOrganization: userDefaultOrgResponse.payload!,
            loggedInUserDataScopeId: loggedInUserDataScopeId,
        });
    };

    getStrategicPlanAssertions = async () => {
        const strategicPlanDataResponse =
            await this.props.zoneContext.zoneApiClient.getStrategicPlanEntityAssertions();
        if (strategicPlanDataResponse.httpResponse.status == 401) {
            window.location.reload();
            return;
        }
        if (strategicPlanDataResponse.errorMessage) {
            this.setState({
                validation: ValidateUtils.parseErrors(
                    strategicPlanDataResponse.errors,
                    strategicPlanDataResponse.errorMessage
                ),
                pendingResponse: false,
            });
            return;
        }

        return strategicPlanDataResponse;
    };

    getHelpText = (originalText?: string) => {
        let text = '';
        if (originalText) {
            const temp = originalText.split('-');
            for (let i = 0; i < temp.length; i++) {
                if (temp[i]) text += '- ' + temp[i] + '\n';
            }
        }

        return text;
    };

    getLastUpdated = () => {
        if (this.state.assertions.filter((x) => x.modifiedDate).length > 0)
            return (
                'Last Updated ' +
                Common.dateTimeFormatToLocal(
                    this.state.assertions
                        .filter((x) => x.modifiedDate)
                        .sort(Sort.compareDate('modifiedDate', 'desc'))[0].modifiedDate
                )
            );
        else return '';
    };

    save = async () => {
        this.setState({ pendingResponse: true, validation: {} });
        const response = await this.props.zoneContext.zoneApiClient.saveEntityAssertions(
            this.state.assertions
        );
        if (response.httpResponse.status == 401) {
            window.location.reload();
            return;
        }
        if (response.errorMessage) {
            this.setState({
                validation: ValidateUtils.parseErrors(response.errors, response.errorMessage),
                pendingResponse: false,
            });
            return;
        }

        const strategicPlanDataResponse = await this.getStrategicPlanAssertions();
        this.setState({
            pendingResponse: false,
            readOnly: true,
            assertions: strategicPlanDataResponse!.payload!,
            originalAssertions: JSON.parse(JSON.stringify(strategicPlanDataResponse!.payload)),
        });
    };

    finalizeChange = (modifiedAssertions: IEntityAssertionModel[]) => {
        if (modifiedAssertions.length == 1) {
            const assertions = this.state.assertions;

            const firstModifiedAssertion = modifiedAssertions[0];
            const match = assertions?.find(
                (assertion) =>
                    assertion.assertionDescriptorGuid ==
                    firstModifiedAssertion.assertionDescriptorGuid
            );

            if (match) {
                match.textValue = firstModifiedAssertion.textValue;
                match.booleanValue = firstModifiedAssertion.booleanValue;
                match.numericValue = firstModifiedAssertion.numericValue;
                match.dateTimeValue = firstModifiedAssertion.dateTimeValue;
                match.assertionDescriptorValueGuid =
                    firstModifiedAssertion.assertionDescriptorValueGuid;

                this.setState({
                    assertions: assertions,
                    currentNarrativeCaseAssertionOnEditMode: undefined,
                });
            }
        }
    };

    handleNarrativeFieldEdit = (item: IEntityAssertionModel) => {
        const match = this.state.assertions.find(
            (assertion) =>
                assertion.guid === item.guid &&
                assertion.assertionDescriptorGuid == item.assertionDescriptorGuid
        );
        if (match)
            this.setState({
                currentNarrativeCaseAssertionOnEditMode: JSON.parse(JSON.stringify(match)),
            });
    };

    handleClearNarrativeFieldEdit = () => {
        if (this.state.currentNarrativeCaseAssertionOnEditMode) {
            const currentNarrativeCaseAssertionOnEditMode =
                this.state.currentNarrativeCaseAssertionOnEditMode;
            currentNarrativeCaseAssertionOnEditMode.textValue = '';
            this.setState({
                currentNarrativeCaseAssertionOnEditMode: currentNarrativeCaseAssertionOnEditMode,
            });
        }
    };

    handleNarrativeFieldTextChange = (
        event:
            | React.ChangeEvent<HTMLInputElement>
            | React.ChangeEvent<HTMLSelectElement>
            | React.ChangeEvent<HTMLTextAreaElement>
    ) => {
        if (this.state.currentNarrativeCaseAssertionOnEditMode) {
            const currentNarrativeCaseAssertionOnEditMode =
                this.state.currentNarrativeCaseAssertionOnEditMode;
            currentNarrativeCaseAssertionOnEditMode.textValue = event.target.value;
            this.setState({
                currentNarrativeCaseAssertionOnEditMode: currentNarrativeCaseAssertionOnEditMode,
            });
        }
    };

    onClickViewAddEditNotesSection = () => {
        this.setState({ showSignificantChanges: !this.state.showSignificantChanges });
    };

    handleAddNote = () => {
        const currentNote: INoteModel = {
            type: { id: NoteTypes.StrategySignificantChange },
            status: { id: NoteStatusTypes.Open },
            organizationGuid: this.props.zoneContext.zoneGuid,
            categories: [{ id: NoteCategories.StrategySignificantChanges }],
            purpose: { id: NotePurposeTypesEnum.Info },
            originStrategyType: StrategyTypes.StrategicPlan.Value,
        };

        this.setState({
            openNoteEditor: true,
            selectedSignificantChange: currentNote,
            showSignificantChanges: false,
        });
    };

    handleEditNote = (e: React.MouseEvent<HTMLButtonElement>, noteGuidToEdit: string) => {
        if (!this.state.significantChanges) return;

        const currentNote = this.state.significantChanges.find((x) => x.guid === noteGuidToEdit);

        if (currentNote) {
            const noteWithStrategyType = {
                ...currentNote,
                originStrategyType: StrategyTypes.StrategicPlan.Value,
            };

            this.setState({
                selectedSignificantChange: noteWithStrategyType,
                openNoteEditor: true,
                showSignificantChanges: false,
            });
        }
    };

    refreshParent = (notes: INoteModel[]) => {
        this.setState({ significantChanges: cloneDeep(notes) });
    };

    handleCancelNoteEditor = () => {
        this.setState({
            significantChanges: cloneDeep(this.state.originalSignificantChanges),
            selectedSignificantChange: undefined,
            openNoteEditor: false,
            showSignificantChanges: true,
        });
    };

    handleSaveNoteComplete = (noteItem: INoteModel) => {
        let significantChangesCopy = cloneDeep(this.state.significantChanges ?? []);

        if (noteItem.guid) {
            const sigChangeIndex = significantChangesCopy.findIndex(
                (sc) => sc.guid === noteItem.guid
            );

            if (sigChangeIndex > -1) {
                significantChangesCopy.splice(sigChangeIndex, 1, noteItem);
            } else {
                significantChangesCopy.push(noteItem);
            }

            significantChangesCopy = significantChangesCopy.sort(
                NoteHelper.sortNotesByStarredAndThenByModifiedDate
            );
        }

        this.setState({
            pendingResponse: false,
            openNoteEditor: false,
            significantChanges: significantChangesCopy,
            originalSignificantChanges: cloneDeep(significantChangesCopy),
            selectedSignificantChange: undefined,
            showSignificantChanges: true,
        });
    };

    getNotesUICustomization = () => {
        const config: INotesUICustomization = {
            hideCategories: true,
            contentHeaderText: 'Impact',
            displayAdditionalInfoAsColumns: true,
            hideFilterByCategory: true,
            hideAuditFields: true,
            hideExport: true,
        };

        return config;
    };

    sortData = (key: string, order: string, subKey?: string, subGrandKey?: string) => {
        if (this.state.significantChanges) {
            let significantChanges: INoteModel[] = [];
            if (key == 'date') {
                // HACK. Only for Signiticant Changes List, Date = Associated Entity Assertion and NOT Modified Date
                const significantChangesTemp: INoteModel[] = JSON.parse(
                    JSON.stringify(this.state.significantChanges)
                );
                {
                    significantChangesTemp.map((item: INoteModel) => {
                        item.modifiedDate = Common.dateFormat(
                            NoteHelper.getAssertionValue(
                                item,
                                'DATE',
                                this.props.assertionDescriptorsSignificantChanges
                            )
                        );
                    });
                }

                const sortedData = significantChangesTemp.sort(
                    Sort.compareDate('modifiedDate', subKey, order)
                );
                sortedData.map((item: INoteModel) => {
                    significantChanges.push(
                        this.state.significantChanges.find((x) => x.guid === item.guid)!
                    );
                });
            } else {
                significantChanges = this.state.significantChanges.sort(
                    Sort.compareValues(key, subKey, order, subGrandKey)
                );
            }

            this.setState({ significantChanges: significantChanges });
        }
    };

    showConfirmRemoveDailog = (note: INoteModel) => {
        this.setState({
            showConfirmRemoveSignificantChangeDialog: true,
            selectedSignificantChangeToDelete: note,
        });
    };

    cancelRemoveSignificantChange = () => {
        this.setState({
            showConfirmRemoveSignificantChangeDialog: false,
            selectedSignificantChangeToDelete: undefined,
        });
    };

    deleteNote = async () => {
        if (!this.state.selectedSignificantChangeToDelete) return;

        let key;
        if (this.state.selectedSignificantChangeToDelete.type!.id == NoteTypes.CaseNote) {
            key = this.state.selectedSignificantChangeToDelete.caseGuid;
        }

        const response = await this.props.zoneContext.zoneApiClient.deleteNote(
            this.state.selectedSignificantChangeToDelete!.guid!,
            key
        );
        if (response.httpResponse.status == 401) {
            window.location.reload();
            return;
        }
        if (response.errorMessage) {
            this.setState({
                validation: ValidateUtils.parseErrors(response.errors, response.errorMessage),
            });
            return;
        }

        const updatedSignificantChanges = this.state.significantChanges.filter(
            (x) => x.guid != this.state.selectedSignificantChangeToDelete!.guid!
        );
        this.setState({
            significantChanges: updatedSignificantChanges,
            originalSignificantChanges: JSON.parse(JSON.stringify(updatedSignificantChanges)),
            showConfirmRemoveSignificantChangeDialog: false,
        });
    };

    handleSignificantChangesExportError = () => {
        const validation = this.state.validation;
        validation.significantChanges = [DisplayMessages.UnexpectedError];
        this.setState({ pendingResponse: false, validation: validation });
    };

    handleExportError = () => {
        const validation = this.state.validation;
        validation.model = [DisplayMessages.UnexpectedError];
        this.setState({ pendingResponse: false, validation: validation });
    };

    render() {
        if (this.state.pendingResponse) return <Loader />;

        return (
            <>
                {this.state.openNoteEditor && this.state.selectedSignificantChange && (
                    <MainContentOverlayComponent>
                        <NoteEditor
                            noteCategoryTypes={this.state.noteCategoryTypes}
                            currentNote={this.state.selectedSignificantChange}
                            onSaveComplete={this.handleSaveNoteComplete}
                            onCancel={this.handleCancelNoteEditor}
                            notesUICustomization={this.getNotesUICustomization()}
                            enableNoteAssociations={true}
                            noteAssociationTitle="Associated Topic(s)"
                            noteAssociationIcon='fa-flag'
                            noteAssociationEntityType={{ id: EntityTypes.AssertionDescriptor }}
                            noteAssociationEntityItems={NoteHelper.getAssociatedAssertionDescriptorListForSignificantChanges(
                                this.state.assertions,
                                this.props.assertionDescriptorsStrategy
                            )}
                            strategyTypeId={StrategyTypes.StrategicPlan.Value}
                        />
                    </MainContentOverlayComponent>
                )}

                <PageWithAffixedHeader
                    printHide={this.state.openNoteEditor}
                    headerComponent={
                        <Stack direction="horizontal" style={{ justifyContent: 'space-between' }}>
                            <h1>
                                Strategic Plan
                                <span className="font-size-xs ms-2">{this.getLastUpdated()}</span>
                            </h1>
                            <div style={{ paddingRight: '1em' }}>
                                {this.state.readOnly ? (
                                    <span>
                                        <button
                                            className="btn btn-no-bg float-end text-gray "
                                            onClick={() =>
                                                this.setState({ showSignificantChanges: true })
                                            }
                                        >
                                            <i className="fal fa-directions" />
                                            &nbsp;Significant Changes
                                        </button>
                                        <ExportFileButton
                                            exportFormat={DocumentExportFormat.PDF} 
                                            url={'/' + ApiRoutes.ExportReport}
                                            reportType={{
                                                id: SystemGeneratedDocumentsTypesEnum.StrategicPlanExport.Value,
                                            }}
                                            onError={this.handleExportError}
                                            icon="fa-file-pdf"
                                        />
                                        {Authorization.userHasRight(
                                            UserRightsEnum.EditStrategicPlan,
                                            this.props.user,
                                            this.props.zoneContext.zoneGuid
                                        ) ? (
                                            <button
                                                className="btn btn-no-bg float-end text-gray "
                                                onClick={() => this.setState({ readOnly: false })}
                                            >
                                                <i className="fal fa-pen" />
                                                &nbsp;Edit
                                            </button>
                                        ) : null}
                                    </span>
                                ) : (
                                    <span>
                                        <button
                                            className="btn btn-default float-end"
                                            onClick={() =>
                                                this.setState({
                                                    assertions: this.state.originalAssertions,
                                                    readOnly: true,
                                                })
                                            }
                                        >
                                            Cancel
                                        </button>
                                        <input
                                            type="button"
                                            className="btn btn-orange float-end "
                                            onClick={this.save}
                                            value="Save"
                                        />
                                    </span>
                                )}
                            </div>
                        </Stack>
                    }
                >
                    <span className="text-danger">{this.state.validation.model}</span>
                    <h5>Situation</h5>
                    <hr className="strategic-plan-line-margin" />
                    {this.state.assertions
                        .filter((entityAssertion) => {
                            const foundAD = this.props.assertionDescriptorsStrategy.find(
                                (ad) => ad.guid === entityAssertion.assertionDescriptorGuid
                            );

                            return (
                                foundAD?.category?.id ===
                                AssertionDescriptorCategories.Strategy_Situation.Value
                            );
                        })
                        .map((entityAssertion: IEntityAssertionModel) => {
                            const foundAD = this.props.assertionDescriptorsStrategy.find(
                                (ad) => ad.guid === entityAssertion.assertionDescriptorGuid
                            );

                            return (
                                <div>
                                    <div className="row">
                                        <div className="col-sm-3">
                                            <label className="text-gray">
                                                {foundAD?.displayName}
                                            </label>
                                            {foundAD?.helpText ? (
                                                <Help
                                                    type={ApplicationHelpType.Info}
                                                    title={
                                                        foundAD?.displayName ?? foundAD.name ?? ''
                                                    }
                                                    helpText={foundAD?.helpText}
                                                />
                                            ) : null}
                                            {this.state.significantChanges &&
                                            NoteHelper.hasSignificantChange(
                                                entityAssertion.assertionDescriptorGuid!,
                                                this.state.significantChanges
                                            ) ? (
                                                <button
                                                    className="btn btn-no-bg"
                                                    onClick={() =>
                                                        this.setState({
                                                            showSignificantChanges: true,
                                                        })
                                                    }
                                                >
                                                    <i className="fal fa-lg fa-directions color-gray" />
                                                </button>
                                            ) : null}
                                        </div>
                                        <div className="col-sm-9 horizontal-padding">
                                            <EntityAssertionControl
                                                assertionDescriptor={foundAD}
                                                assertionDescriptorUsageType={
                                                    AssertionDescriptorUsageTypesEnum.Strategy
                                                }
                                                entityAssertions={[entityAssertion]}
                                                editMode={!this.state.readOnly}
                                                refreshChange={this.finalizeChange}
                                                handleNarrativeFieldEdit={
                                                    this.handleNarrativeFieldEdit
                                                }
                                                narrativeFieldAsPopup={true}
                                            />
                                        </div>
                                    </div>
                                    {this.state.readOnly ? (
                                        /*&& index != (this.state.assertions.filter(x => x.assertionDescriptor && x.assertionDescriptor.category && x.assertionDescriptor.category.id == AssertionDescriptorCategories.Strategy_Situation.Value).length - 1)*/
                                        <hr className="thin-top-border strategic-plan-line-margin" />
                                    ) : null}
                                </div>
                            );
                        })}
                    <h5>Goals</h5>
                    <hr className="strategic-plan-line-margin" />
                    {this.state.assertions
                        .filter((entityAssertion) => {
                            const foundAD = this.props.assertionDescriptorsStrategy.find(
                                (ad) => ad.guid === entityAssertion.assertionDescriptorGuid
                            );

                            return (
                                foundAD?.category?.id ===
                                AssertionDescriptorCategories.Strategy_Goals.Value
                            );
                        })
                        .map((entityAssertion: IEntityAssertionModel) => {
                            const foundAD = this.props.assertionDescriptorsStrategy.find(
                                (ad) => ad.guid === entityAssertion.assertionDescriptorGuid
                            );

                            return (
                                <div>
                                    <div className="row">
                                        <div className="col-sm-3">
                                            <label className="text-gray">
                                                {foundAD?.displayName}
                                            </label>
                                            {foundAD?.helpText ? (
                                                <Help
                                                    type={ApplicationHelpType.Info}
                                                    title={
                                                        foundAD?.displayName ?? foundAD.name ?? ''
                                                    }
                                                    helpText={foundAD?.helpText}
                                                />
                                            ) : null}
                                            {this.state.significantChanges &&
                                            NoteHelper.hasSignificantChange(
                                                entityAssertion.assertionDescriptorGuid!,
                                                this.state.significantChanges
                                            ) ? (
                                                <button
                                                    className="btn btn-no-bg"
                                                    onClick={() =>
                                                        this.setState({
                                                            showSignificantChanges: true,
                                                        })
                                                    }
                                                >
                                                    <i className="fal fa-lg fa-directions color-gray" />
                                                </button>
                                            ) : null}
                                        </div>
                                        <div className="col-sm-9 horizontal-padding">
                                            <EntityAssertionControl
                                                assertionDescriptor={foundAD}
                                                assertionDescriptorUsageType={
                                                    AssertionDescriptorUsageTypesEnum.Strategy
                                                }
                                                entityAssertions={[entityAssertion]}
                                                editMode={!this.state.readOnly}
                                                refreshChange={this.finalizeChange}
                                                handleNarrativeFieldEdit={
                                                    this.handleNarrativeFieldEdit
                                                }
                                                narrativeFieldAsPopup={true}
                                            />
                                        </div>
                                    </div>
                                    {this.state.readOnly && (
                                        <hr className="thin-top-border strategic-plan-line-margin" />
                                    )}
                                </div>
                            );
                        })}
                    <h5>Principles</h5>
                    <hr className="strategic-plan-line-margin" />
                    {this.state.assertions
                        .filter((entityAssertion) => {
                            const foundAD = this.props.assertionDescriptorsStrategy.find(
                                (ad) => ad.guid === entityAssertion.assertionDescriptorGuid
                            );

                            return (
                                foundAD?.category?.id ===
                                AssertionDescriptorCategories.Strategy_Principles.Value
                            );
                        })
                        .map((entityAssertion: IEntityAssertionModel) => {
                            const foundAD = this.props.assertionDescriptorsStrategy.find(
                                (ad) => ad.guid === entityAssertion.assertionDescriptorGuid
                            );

                            return (
                                <div>
                                    <div className="row">
                                        <div className="col-sm-3">
                                            <label className="text-gray">
                                                {foundAD?.displayName}
                                            </label>
                                            {foundAD?.helpText ? (
                                                <Help
                                                    type={ApplicationHelpType.Info}
                                                    title={
                                                        foundAD?.displayName ?? foundAD.name ?? ''
                                                    }
                                                    helpText={foundAD?.helpText}
                                                />
                                            ) : null}
                                            {this.state.significantChanges &&
                                            NoteHelper.hasSignificantChange(
                                                entityAssertion.assertionDescriptorGuid!,
                                                this.state.significantChanges
                                            ) ? (
                                                <button
                                                    className="btn btn-no-bg"
                                                    onClick={() =>
                                                        this.setState({
                                                            showSignificantChanges: true,
                                                        })
                                                    }
                                                >
                                                    <i className="fal fa-lg fa-directions color-gray" />
                                                </button>
                                            ) : null}
                                        </div>
                                        <div className="col-sm-9 horizontal-padding">
                                            <EntityAssertionControl
                                                assertionDescriptor={foundAD}
                                                assertionDescriptorUsageType={
                                                    AssertionDescriptorUsageTypesEnum.Strategy
                                                }
                                                entityAssertions={[entityAssertion]}
                                                editMode={!this.state.readOnly}
                                                refreshChange={this.finalizeChange}
                                                handleNarrativeFieldEdit={
                                                    this.handleNarrativeFieldEdit
                                                }
                                                narrativeFieldAsPopup={true}
                                            />
                                        </div>
                                    </div>
                                    {this.state.readOnly ? (
                                        /*&& index != (this.state.assertions.filter(x => x.assertionDescriptor && x.assertionDescriptor.category && x.assertionDescriptor.category.id == AssertionDescriptorCategories.Strategy_Principles.Value).length - 1) */
                                        <hr className="thin-top-border strategic-plan-line-margin" />
                                    ) : null}
                                </div>
                            );
                        })}

                    {this.state.currentNarrativeCaseAssertionOnEditMode && (
                        <EditNarrativeModal
                            onFinalizeChange={this.finalizeChange}
                            assertionDescriptors={this.props.assertionDescriptorsStrategy}
                            entityAssertion={this.state.currentNarrativeCaseAssertionOnEditMode}
                            onCancel={() =>
                                this.setState({
                                    currentNarrativeCaseAssertionOnEditMode: undefined,
                                })
                            }
                            onClear={this.handleClearNarrativeFieldEdit}
                            onNarrativeFieldTextChange={this.handleNarrativeFieldTextChange}
                            validation={this.state.validation}
                        />
                    )}

                    {this.state.showSignificantChanges && (
                        <Modal
                            centered
                            size="xl"
                            show={true}
                            onHide={() =>
                                this.setState({
                                    showSignificantChanges: false,
                                    selectedSignificantChange: undefined,
                                    openNoteEditor: false,
                                })
                            }
                            backdrop={false}
                            dialogClassName="add-task-dialog"
                        >
                            <Modal.Header>
                                <Modal.Title className="w-100">
                                    <div className="row">
                                        <div className="col-sm-6">Significant Changes</div>
                                        <div className="col-sm-6">
                                            {Authorization.userHasRight(
                                                UserRightsEnum.EditStrategicPlan,
                                                this.props.user,
                                                this.props.zoneContext.zoneGuid
                                            ) ? (
                                                <button
                                                    className="btn-no-bg float-end  "
                                                    onClick={this.handleAddNote}
                                                >
                                                    {' '}
                                                    <span className="btn-green btn float-end btn-icon">
                                                        <i className="fal fa-lg fa-plus color-white" />
                                                    </span>
                                                </button>
                                            ) : null}
                                        </div>
                                    </div>
                                </Modal.Title>
                            </Modal.Header>
                            <Modal.Body>
                                <span className="text-danger">
                                    {this.state.validation.significantChanges}
                                </span>
                                <table className="table">
                                    <thead>
                                        <tr>
                                            <th className="col-sm-2 ">Change</th>
                                            <SortableHeader
                                                headerText="Date"
                                                sortKey="date"
                                                onSort={this.sortData}
                                                thClassName="col-sm-1"
                                            />
                                            <th>Impact</th>
                                            <th className="col-sm-2 ">Associated Topic(s)</th>
                                            {Authorization.userHasRight(
                                                UserRightsEnum.EditStrategicPlan,
                                                this.props.user,
                                                this.props.zoneContext.zoneGuid
                                            ) ? (
                                                <th className="col-sm-1"></th>
                                            ) : null}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {this.state.significantChanges.map((item: INoteModel) => {
                                            return (
                                                <tr key={item.guid}>
                                                    <td className="col-sm-3">
                                                        {NoteHelper.getAssertionValue(
                                                            item,
                                                            'CHANGE',
                                                            this.props.assertionDescriptorsSignificantChanges
                                                        )}
                                                    </td>
                                                    <td className="col-sm-1 ">
                                                        {Common.dateFormat(
                                                            NoteHelper.getAssertionValue(
                                                                item,
                                                                'DATE',
                                                                this.props.assertionDescriptorsSignificantChanges
                                                            )
                                                        )}
                                                    </td>
                                                    <td>
                                                        {item.content
                                                            ?.split('\n')
                                                            .map((item, key) => {
                                                                return (
                                                                    <span key={key}>
                                                                        {item}
                                                                        <br />
                                                                    </span>
                                                                );
                                                            })}
                                                    </td>
                                                    <td className="col-sm-3 ">
                                                        {NoteHelper.getAssociatedTopicsForSignificantChanges(
                                                            item
                                                        )
                                                            .split('\n')
                                                            .map((item, key) => {
                                                                return (
                                                                    <span key={key}>
                                                                        {item}
                                                                        <br />
                                                                    </span>
                                                                );
                                                            })}
                                                    </td>
                                                    {Authorization.userHasRight(
                                                        UserRightsEnum.EditStrategicPlan,
                                                        this.props.user,
                                                        this.props.zoneContext.zoneGuid
                                                    ) ? (
                                                        <td className="col-sm-2 ">
                                                            <button
                                                                className="btn btn-no-bg float-end"
                                                                onClick={() => {
                                                                    this.showConfirmRemoveDailog(
                                                                        item
                                                                    );
                                                                }}
                                                            >
                                                                <i className="fal fa-lg fa-trash-alt"></i>
                                                            </button>

                                                            {Authorization.userHasRight(
                                                                UserRightsEnum.EditCaseNote,
                                                                this.props.user,
                                                                this.props.zoneContext.zoneGuid
                                                            ) &&
                                                            (this.state.loggedInUserDataScopeId !==
                                                                DataScopesEnum.LocalBasic.Value ||
                                                                item.allowLocalEdit) ? (
                                                                <button
                                                                    className="btn btn-no-bg float-end"
                                                                    onClick={(
                                                                        e: React.MouseEvent<HTMLButtonElement>
                                                                    ) => {
                                                                        this.handleEditNote(
                                                                            e,
                                                                            item.guid!
                                                                        );
                                                                    }}
                                                                >
                                                                    <i className="fal fa-lg fa-pen" />
                                                                </button>
                                                            ) : null}
                                                        </td>
                                                    ) : null}
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                                <div className="dialog-btn-div margin-top-sm">
                                    <button
                                        className="btn btn-default float-end"
                                        onClick={() =>
                                            this.setState({
                                                showSignificantChanges: false,
                                                selectedSignificantChange: undefined,
                                                openNoteEditor: false,
                                            })
                                        }
                                    >
                                        Close
                                    </button>
                                </div>
                            </Modal.Body>
                        </Modal>
                    )}

                    <CheckmateDialog
                        isShowingModal={
                            this.state.showConfirmRemoveSignificantChangeDialog || false
                        }
                        body="Are you sure you want to delete the selected item?"
                        handleClose={this.cancelRemoveSignificantChange}
                        handleConfirm={this.deleteNote}
                        confirmText="Delete"
                        cancelText="Cancel"
                        confirmButtonClassName="btn btn-black float-end "
                        dialogClassName="confirm-document-delete-dialog"
                        closeButtonClassName="btn btn-default float-end"
                        title="Delete"
                    />
                </PageWithAffixedHeader>
            </>
        );
    }
}
